import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";

import { Button } from "@pentabd/ui";
import {
  CancelButton,
  ErrorModal,
  InputTextRow,
  MultiFileRow,
  SelectControllerRow,
  SuccessModal,
} from "@components";

import useActionCreator from "@redux/hooks/useActionCreator";
import useCustomSelector from "@redux/hooks/useCustomSelector";

import {
  AnnualProcurementResponseType,
  AnnualProcurementCreateType,
} from "@type/procurements/annualProcurementPlan";
import { ROUTES } from "@constants/routes";
import { SETTINGS } from "@constants/procurements/settings";
import { useSettings } from "@hooks/procurements/useSettings";
import { getConvertedMetaData } from "@helpers/inventory/metaDataConvert";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

const { BUDGET_TYPE } = SETTINGS;

export default function AddNewAnnualProcurementPlanForm({
  id,
  procurement,
}: {
  id: string | number;
  procurement: AnnualProcurementResponseType | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { settings: budgetType } = useSettings(BUDGET_TYPE);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { createSuccess, createError, updateSuccess, updateError } =
    useCustomSelector((store) => store.procurements.annualProcurementPlanState); ///!!! use hook

  const createSuccessAction = () =>
    navigate(`${ROUTES.PROCUREMENT.ANNUAL_PROCUREMENT_PLAN}`);
  const closeErrorModal = () => setIsErrorModalOpen(false);

  const { appCreateActionHandler, appUpdateActionHandler } = useActionCreator();

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<AnnualProcurementCreateType>({
    values: revertMultipleDocumentData(procurement, [
      "documents",
    ]) as AnnualProcurementCreateType,
  });

  const onSubmit: SubmitHandler<AnnualProcurementCreateType> = (data) => {
    if (id) {
      appUpdateActionHandler(
        id,
        convertMultipleDocumentData(data, ["documents"]),
      );
    } else {
      appCreateActionHandler(convertMultipleDocumentData(data, ["documents"]));
    }
  };

  useEffect(() => {
    switch (true) {
      case createSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case updateSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case !!createError: {
        setIsErrorModalOpen(true);
        break;
      }
      case !!updateError: {
        setIsErrorModalOpen(true);
        break;
      }
      default: {
        setIsSuccessModalOpen(false);
        break;
      }
    }
  }, [createSuccess, createError, updateSuccess, updateError]);

  return (
    <div>
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
      {/* modal end */}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="container p-10">
          <div className="d-flex flex-column gap-10 pb-10 border-bottom">
            <InputTextRow
              label="PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_1"
              register={{
                ...register("title", {
                  required: "Name is required",
                }),
              }}
              error={errors as any}
              required
            />

            <InputTextRow
              label="PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_2"
              register={{
                ...register("financialYear", {
                  required: "Year is required",
                  pattern: {
                    value: /^\d{4}-\d{4}$/,
                    message: "Provide the correct format e.g: 2022-2023",
                  },
                }),
              }}
              error={errors as any}
              required
            />

            <InputTextRow
              label="PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_3"
              register={{
                ...register("budget", {
                  required: "Total Budget is required",
                  valueAsNumber: true,
                }),
              }}
              error={errors as any}
              type="number"
              suffix={t("PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_3.SUFFIX")}
              required
            />

            <InputTextRow
              label="PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_4"
              register={{
                ...register("appRefNo", {
                  required: "APP Ref No. is required",
                }),
              }}
              error={errors as any}
              required
            />

            <SelectControllerRow
              label="PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_5"
              control={control}
              name={"budgetType"}
              register={{ ...register("budgetType") }}
              options={getConvertedMetaData(budgetType)}
              setValue={setValue}
              required
            />

            <MultiFileRow
              name={"documents"}
              control={control}
              setValue={setValue}
              register={register}
              fileDetails={["documentName", "date", "remarks", "memorialNo"]}
            />

            {/* <div className="row">
              <div className="col-md-12 col-lg-3 d-flex">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_6.LABEL")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <MultiFile
                  {...register("documents", {
                    required: "Attachment is required",
                  })}
                  id={"documents"}
                  control={control}
                  setValue={setValue}
                  register={register}
                  errors={errors as any}
                />
              </div>
            </div> */}
          </div>

          <div className="col-md-12 col-lg-10 d-flex justify-content-end mt-10 gap-8">
            <CancelButton />

            <Button fill="fill" htmlType="submit" type="primary" size="sm">
              {t("PROCUREMENT_ADD_NEW.FORM.SAVE_BUTTON.BUTTON_TEXT")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
